import { BulkButton, BulkSelectionSection } from '../../../widgets/bulkSelection';
import { Columns } from '../../../widgets/columns';
import { EmptyList } from '../../../widgets/emptyList';
import { QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useMutation, usePaginationFragment } from 'react-relay';
import { Center } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { useInfiniteScroll } from 'src/shared/hooks';
import FolderOpenIcon from 'src/shared/material_icons/file/folder_open.svg';
import FolderIcon from 'src/shared/material_icons/file/folder.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import styled from 'styled-components';
import { ImageLightbox } from '../components/layout/imageLightbox';
import { MoveToCategoryDialog } from '../components/results/moveToCategoryDialog';
import { assetLibraryQueriesDeleteAIAssetLibraryAssetMutation } from './__generated__/assetLibraryQueriesDeleteAIAssetLibraryAssetMutation.graphql';
import { assetLibraryQueriesUpdateAIAssetLibraryAssetMutation } from './__generated__/assetLibraryQueriesUpdateAIAssetLibraryAssetMutation.graphql';
import { assetsGrid_query$key } from './__generated__/assetsGrid_query.graphql';
import { assetsGridPaginationQuery } from './__generated__/assetsGridPaginationQuery.graphql';
import { AssetCardSkeleton } from './assetGridSkeleton';
import { AssetItem } from './assetItem';
import { deleteAIAssetLibraryAssetMutation, updateAIAssetLibraryAssetMutation } from './assetLibraryQueries';

const assetsGridFragment = graphql`
    fragment assetsGrid_query on Query
    @refetchable(queryName: "assetsGridPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 50 }
        cursor: { type: "String" }
        categoryID: { type: "Int" }
        assetType: { type: "String" }
        sourceToolID: { type: "String" }
        searchName: { type: "String" }
        sortBy: { type: "AIAssetLibrarySortBy" }
        sortDirection: { type: "SortDirection" }
    ) {
        aiAssetLibraryAssets(
            first: $count
            after: $cursor
            categoryID: $categoryID
            assetType: $assetType
            sourceToolID: $sourceToolID
            searchName: $searchName
            sortBy: $sortBy
            sortDirection: $sortDirection
        ) @connection(key: "assetsGrid_aiAssetLibraryAssets") {
            edges {
                node {
                    id
                    categoryID
                    name
                    assetType
                    sourceToolID
                    fileServerID
                    filePath
                    fileName
                    fileUrl
                    fileSizeBytes
                    width
                    height
                    durationSeconds
                    thumbnailUrl
                    metadata {
                        toolId
                        toolName
                        prompt
                        negativePrompt
                        model
                        width
                        height
                        seed
                        strength
                        steps
                        guidanceScale
                        sourceImageUrls
                        styleImageUrl
                        maskImageUrl
                        backgroundPreset
                        backgroundImageUrl
                        personPreset
                        personImageUrl
                        clothingPreset
                        clothingImageUrl
                        posePreset
                        effectPreset
                        surfacePreset
                        moodPreset
                        materialPreset
                        colorPreset
                        layoutPreset
                        stylePreset
                        videoPreset
                        scaleFactor
                        durationSeconds
                        resolution
                        aspectRatio
                        generateAudio
                        language
                        title
                        subtitle
                        ctaText
                        savedAt
                    }
                    createdDate
                    modifiedDate
                    categoryName
                }
            }
        }
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: auto;
`;

const GridContainer = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    justify-content: center;
`;

const ColumnWrapper = styled.div`
    padding: 20px;
    width: 100%;
    align-self: flex-start;
`;

const ToolbarRow = styled.div`
    position: sticky;
    top: 0;
    z-index: 3;
    padding: 8px 20px;
    background: ${(props) => props.theme.app.backgroundColorBase};
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const ToolbarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
`;

interface AssetsGridProps {
    query: assetsGrid_query$key;
    selectedCategoryId?: string | null;
    /** Callback when user wants to use an asset as input for a tool */
    onUseAsInput?: (imageUrl: string) => void;
    /** Search/sort controls rendered on the right side of the toolbar */
    toolbarRight?: React.ReactNode;
}

export const AssetsGrid: FC<AssetsGridProps> = ({ query, selectedCategoryId, onUseAsInput, toolbarRight }) => {
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    const [openMenuAssetId, setOpenMenuAssetId] = useState<string | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { showDialog, closeDialog } = useDialogManager();

    const [deleteAsset] = useMutation<assetLibraryQueriesDeleteAIAssetLibraryAssetMutation>(deleteAIAssetLibraryAssetMutation);
    const [updateAsset] = useMutation<assetLibraryQueriesUpdateAIAssetLibraryAssetMutation>(updateAIAssetLibraryAssetMutation);

    const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<assetsGridPaginationQuery, assetsGrid_query$key>(
        assetsGridFragment,
        query
    );

    const assets = (data.aiAssetLibraryAssets?.edges || []).map((edge) => edge.node);

    useInfiniteScroll(() => loadNext(50), hasNext, containerRef.current);

    const handleRefetch = useCallback(() => {
        refetch({}, { fetchPolicy: 'network-only' });
    }, [refetch]);

    // Close dropdown menu on scroll
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !openMenuAssetId) return;

        const handleScroll = () => setOpenMenuAssetId(null);
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [openMenuAssetId]);

    const handleMenuOpen = useCallback((id: string) => {
        setOpenMenuAssetId(id);
    }, []);

    const handleMenuClose = useCallback(() => {
        setOpenMenuAssetId(null);
    }, []);

    const handleToggleSelect = useCallback((id: string) => {
        setSelectedAssetIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }, []);

    const handleLightboxNext = useCallback(() => {
        setLightboxIndex((prev) => (prev !== null && prev < assets.length - 1 ? prev + 1 : prev));
    }, [assets.length]);

    const handleLightboxPrevious = useCallback(() => {
        setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
    }, []);

    const allSelected = assets.length > 0 && selectedAssetIds.length === assets.length;

    const handleSelectAll = useCallback(() => {
        if (allSelected) {
            setSelectedAssetIds([]);
        } else {
            setSelectedAssetIds(assets.map((a) => a.id));
        }
    }, [allSelected, assets]);

    const handleBulkDelete = useCallback(() => {
        const count = selectedAssetIds.length;
        showDialog(
            'bulk-delete-assets',
            <QuestionDialog
                destructive
                message={T._(`Are you sure you want to delete ${count} asset(s)? This action cannot be undone.`, 'Admin')}
                onYesClick={() => {
                    closeDialog('bulk-delete-assets');
                    const notificationId = 'bulk-delete-progress';
                    showInfoNotification({ id: notificationId, message: T._('Deleting assets...', 'Admin') });

                    let completed = 0;
                    let errors = 0;

                    selectedAssetIds.forEach((id) => {
                        deleteAsset({
                            variables: { id },
                            onCompleted: () => {
                                completed++;
                                if (completed + errors === count) {
                                    removeNotification(notificationId);
                                    if (errors > 0) {
                                        showErrorNotification({ message: T._(`Failed to delete ${errors} asset(s).`, 'Admin') });
                                    } else {
                                        showSuccessNotification({ message: T._(`${count} asset(s) deleted.`, 'Admin') });
                                    }
                                    setSelectedAssetIds([]);
                                    handleRefetch();
                                }
                            },
                            onError: () => {
                                errors++;
                                if (completed + errors === count) {
                                    removeNotification(notificationId);
                                    showErrorNotification({ message: T._(`Failed to delete ${errors} asset(s).`, 'Admin') });
                                    setSelectedAssetIds([]);
                                    handleRefetch();
                                }
                            },
                            updater: (store) => {
                                store.invalidateStore();
                            }
                        });
                    });
                }}
            />
        );
    }, [selectedAssetIds, showDialog, closeDialog, deleteAsset, handleRefetch]);

    const handleBulkMove = useCallback(() => {
        const count = selectedAssetIds.length;
        showDialog(
            'bulk-move-assets',
            <MoveToCategoryDialog
                currentCategoryId={null}
                onMove={(categoryId: number | null) => {
                    closeDialog('bulk-move-assets');
                    const notificationId = 'bulk-move-progress';
                    showInfoNotification({ id: notificationId, message: T._('Moving assets...', 'Admin') });

                    let completed = 0;
                    let errors = 0;

                    // Find asset names for the update mutation
                    selectedAssetIds.forEach((id) => {
                        const asset = assets.find((a) => a.id === id);
                        if (!asset) {
                            errors++;
                            return;
                        }

                        updateAsset({
                            variables: {
                                id,
                                name: asset.name,
                                categoryID: categoryId
                            },
                            onCompleted: () => {
                                completed++;
                                if (completed + errors === count) {
                                    removeNotification(notificationId);
                                    if (errors > 0) {
                                        showErrorNotification({ message: T._(`Failed to move ${errors} asset(s).`, 'Admin') });
                                    } else {
                                        showSuccessNotification({ message: T._(`${count} asset(s) moved.`, 'Admin') });
                                    }
                                    setSelectedAssetIds([]);
                                    handleRefetch();
                                }
                            },
                            onError: () => {
                                errors++;
                                if (completed + errors === count) {
                                    removeNotification(notificationId);
                                    showErrorNotification({ message: T._(`Failed to move ${errors} asset(s).`, 'Admin') });
                                    setSelectedAssetIds([]);
                                    handleRefetch();
                                }
                            },
                            updater: (store) => {
                                store.invalidateStore();
                            }
                        });
                    });
                }}
                onClose={() => closeDialog('bulk-move-assets')}
            />
        );
    }, [selectedAssetIds, assets, showDialog, closeDialog, updateAsset, handleRefetch]);

    const handleBulkAction = useCallback(
        (button: BulkButton) => {
            switch (button.key) {
                case 'delete':
                    handleBulkDelete();
                    break;
                case 'move':
                    handleBulkMove();
                    break;
            }
        },
        [handleBulkDelete, handleBulkMove]
    );

    const showBulkButtons = selectedAssetIds.length >= 2;

    const bulkButtons: BulkButton[] = [
        {
            key: 'select-all',
            label: T._('Select all', 'Admin'),
            isCheckbox: true,
            checked: allSelected,
            onClick: handleSelectAll
        },
        ...(showBulkButtons
            ? [
                  {
                      key: 'delete',
                      label: T._('Delete', 'Admin'),
                      tooltip: T._('Delete selected assets', 'Admin'),
                      icon: <TrashIcon />,
                      onClick: handleBulkAction
                  },
                  { key: 'move', label: T._('Move', 'Admin'), tooltip: T._('Move selected assets', 'Admin'), icon: <FolderIcon />, onClick: handleBulkAction }
              ]
            : [])
    ];

    return (
        <Container ref={containerRef}>
            <ToolbarRow>
                {assets.length > 0 && <BulkSelectionSection buttons={bulkButtons} />}
                <ToolbarRight>{toolbarRight}</ToolbarRight>
            </ToolbarRow>
            <GridContainer>
                {assets.length === 0 ? (
                    <Center>
                        <EmptyList
                            icon={selectedCategoryId ? <FolderOpenIcon /> : <ImageIcon />}
                            title={selectedCategoryId ? T._('No assets in this category', 'Admin') : T._('No assets yet', 'Admin')}
                            subTitle={<span>{T._('Save outputs from AI Studio tools to your library for easy access and organization.', 'Admin')}</span>}
                        />
                    </Center>
                ) : (
                    <ColumnWrapper>
                        <Columns $xs={2} $md={3} $lg={8} $gap={20}>
                            {assets.map((asset, index) => (
                                <AssetItem
                                    key={asset.id}
                                    asset={asset}
                                    onAssetChange={handleRefetch}
                                    onUseAsInput={onUseAsInput}
                                    isSelected={selectedAssetIds.includes(asset.id)}
                                    onToggleSelect={handleToggleSelect}
                                    isMenuOpen={openMenuAssetId === asset.id}
                                    onMenuOpen={handleMenuOpen}
                                    onMenuClose={handleMenuClose}
                                    onExpand={() => setLightboxIndex(index)}
                                />
                            ))}
                            {isLoadingNext && Array.from({ length: 50 }).map((_, i) => <AssetCardSkeleton key={`skeleton-${i}`} />)}
                        </Columns>
                    </ColumnWrapper>
                )}
            </GridContainer>
            {lightboxIndex !== null && assets[lightboxIndex] && (
                <ImageLightbox
                    imageUrl={assets[lightboxIndex].fileUrl}
                    alt={assets[lightboxIndex].name}
                    isVideo={assets[lightboxIndex].assetType === 'video'}
                    isModel={assets[lightboxIndex].assetType === '3d-model'}
                    onClose={() => setLightboxIndex(null)}
                    onNext={lightboxIndex < assets.length - 1 ? handleLightboxNext : undefined}
                    onPrevious={lightboxIndex > 0 ? handleLightboxPrevious : undefined}
                />
            )}
        </Container>
    );
};
